import clsx from "clsx";
import { CircleAlert, FileJson, Upload, Check } from "lucide-react";
import React from "react";
import PropTypes from "prop-types";

export function JsonFileDropzone({ validateFields, onFile }) {
  const [errorMessage, setErrorMessage] = React.useState(null);
  const [fileName, setFileName] = React.useState(null);
  const [isDragging, setIsDragging] = React.useState(false);
  const [isValid, setIsValid] = React.useState(false);
  const fileInputRef = React.useRef(null);

  function processFile(file) {
    setFileName(file.name);
    setIsValid(false);
    var reader = new FileReader();
    reader.onload = function (event) {
      try {
        setErrorMessage(null);
        var parsed = JSON.parse(event.target.result);

        for (var i = 0; i < validateFields.length; i++) {
          if (!(validateFields[i] in parsed)) {
            throw new Error(
              "Invalid credentials file. Missing json key: " +
                validateFields[i],
            );
          }
        }

        setIsValid(true);
        onFile(parsed);
      } catch (error) {
        console.error(error);
        setErrorMessage(error.message);
        onFile(null);
      }
    };
    reader.readAsText(file);
  }

  function onChange(event) {
    const file = event.target.files[0];
    if (file) processFile(file);
  }

  function onDragOver(e) {
    e.preventDefault();
    setIsDragging(true);
  }

  function onDragLeave() {
    setIsDragging(false);
  }

  function onDrop(e) {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files[0];
    if (file) processFile(file);
  }

  return (
    <>
      <input
        ref={fileInputRef}
        type="file"
        accept=".json"
        onChange={onChange}
        style={{ display: "none" }}
      />
      <div
        onDragOver={onDragOver}
        onDragLeave={onDragLeave}
        onDrop={onDrop}
        onClick={() => fileInputRef.current.click()}
        className={clsx(
          "mt-3 flex cursor-pointer flex-col items-center justify-center rounded-lg border-2 border-dashed p-6 text-center transition-colors",
          isDragging
            ? "border-blue-400 bg-blue-50"
            : errorMessage
              ? "border-red-300 bg-red-50 hover:border-red-400"
              : isValid
                ? "border-green-300 bg-white hover:border-green-400 dark:bg-gray-100"
                : "border-gray-300 bg-white hover:border-gray-400 hover:bg-gray-50 dark:bg-gray-100",
        )}
      >
        <div
          className={clsx(
            "mb-3 rounded-full p-3",
            errorMessage ? "bg-red-50" : isValid ? "bg-green-50" : "bg-blue-50",
          )}
        >
          {errorMessage ? (
            <CircleAlert size={20} className="text-red-500" />
          ) : isValid ? (
            <Check size={20} className="text-green-500" />
          ) : (
            <Upload size={20} className="text-blue-500" />
          )}
        </div>
        {errorMessage ? (
          <>
            <div className="flex items-center gap-2 font-medium text-red-600">
              <FileJson size={16} className="shrink-0 text-red-400" />
              <span className="truncate">{fileName}</span>
            </div>
            <p className="mt-2 text-sm text-red-500">{errorMessage}</p>
            <p className="mt-1 text-xs text-red-400">
              Click or drop to try again
            </p>
          </>
        ) : fileName ? (
          <>
            <div className="flex items-center gap-2 font-medium text-gray-700">
              <FileJson size={16} className="shrink-0 text-gray-400" />
              <span className="truncate">{fileName}</span>
            </div>
            <p className="mt-1 text-xs text-gray-400">
              Click or drop to replace
            </p>
          </>
        ) : (
          <>
            <p className="font-medium text-gray-700">
              Drag or drop your JSON file here
            </p>
            <p className="mt-1 text-sm text-gray-500">
              Service account credentials from Google Cloud Console
            </p>
            <p className="mt-3 text-xs text-gray-400">.json files only</p>
          </>
        )}
      </div>
    </>
  );
}

JsonFileDropzone.propTypes = {
  validateFields: PropTypes.arrayOf(PropTypes.string).isRequired,
  onFile: PropTypes.func.isRequired,
};
